import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EnvironmentInjector,
  Input,
  OnDestroy,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { ModalData, ModalService } from '@app/components/modal/modal.service';
import { ModalButtonsComponent } from '@app/components/modal/modalButtons.component';

@Component({
  selector: 'app-modal-renderer',
  standalone: true,
  imports: [CommonModule, ModalButtonsComponent],
  template: `
    <div
      [attr.class]="modal.options?.class"
      class="modal-overlay print:bottom-none fixed inset-0 z-[1002] flex print:absolute"
      [ngClass]="{
        'items-center justify-center': modal.options?.screen === 'auto' || modal.options?.screen === 'aside',
      }"
      role="dialog"
      [attr.aria-modal]="true"
      data-testid="modal-content">
      <div #modalOverlay class="absolute inset-0 bg-black bg-opacity-50"></div>
      <div
        class="relative flex flex-col bg-white"
        [ngClass]="{
          'max-h-[97dvh] max-w-[calc(100dvw-2rem)] rounded-xl shadow-lg': modal.options?.screen === 'auto',
          'h-full max-h-full max-w-full flex-1': modal.options?.screen === 'full',
          'ml-0 mr-0 h-full max-h-full w-full md:ml-auto md:w-3/4 xl:w-1/2 2xl:w-1/3': modal.options?.screen === 'aside',
          'pt-8 md:pt-12': modal.options?.showCloseButton,
        }"
        tabindex="-1">
        <div class="print-full-height flex flex-1 overflow-auto">
          <ng-template #container></ng-template>
        </div>

        <app-modal-buttons
          [showPrintButton]="modal.options?.showPrintButton ?? false"
          [showCloseButton]="modal.options?.showCloseButton ?? true"
          (closeClick)="onCloseButtonClick($event)"
          class="!absolute right-2 top-4 md:right-6 md:top-6 print:hidden" />
      </div>
    </div>
  `,
  styles: [
    `
      .the-default-display:not(.block):not(.inline):not(.inline-block):not(.flex):not(.inline-flex):not(.grid):not(.inline-grid):not(.hidden):not(
          .table
        ):not(.table-row):not(.table-cell) {
        display: var(--host-display, contents);
      }
    `,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class ModalRendererComponent implements AfterViewInit, OnDestroy {
  @Input({ required: true }) modal!: ModalData;

  @ViewChild('container', { read: ViewContainerRef }) container!: ViewContainerRef;
  @ViewChild('modalOverlay', { read: ElementRef }) modalOverlay!: ElementRef;

  modalService = inject(ModalService);
  environmentInjector = inject(EnvironmentInjector);

  private _documentKeyDownListener?: (event: KeyboardEvent) => void;

  ngAfterViewInit(): void {
    this._insertComponent();
    this._setupEventListeners();
  }

  ngOnDestroy(): void {
    if (this._documentKeyDownListener) {
      document.removeEventListener('keydown', this._documentKeyDownListener);
    }
  }

  onCloseButtonClick(event: Event) {
    event?.preventDefault();
    event?.stopPropagation();

    if (this.modal.options?.onCloseButtonClick) {
      return this.modal.options.onCloseButtonClick({ ref: this.modal.id, close: () => this.modalService.close(this.modal.id), event });
    }
    this.modalService.close(this.modal.id);
  }

  okClick(event?: any) {
    if (this.modal.options?.onOkButtonClick) {
      return this.modal.options.onOkButtonClick({ ref: this.modal.id, close: () => this.modalService.close(this.modal.id), event });
    }
    this.modalService.close(this.modal.id);
  }

  private _onEscape(event: KeyboardEvent) {
    if (this.modal.options?.closeOnEscape === false) {
      return;
    }
    if (this.modal.options?.onEscape) {
      return this.modal.options.onEscape({ event: event, ref: this.modal.id, close: () => this.modalService.close(this.modal.id) });
    }
    this.modalService.close(this.modal.id);
  }

  private _onOverlayKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this._onEscape(event);
    }
  }

  private _onOverlayClick(event?: MouseEvent | KeyboardEvent) {
    const target = event?.target as HTMLElement;
    if (target !== this.modalOverlay?.nativeElement) {
      return;
    }
    if (this.modal.options?.closeOnEscape === false) {
      return;
    }
    if (this.modal.options?.onBackdropClick) {
      return this.modal.options.onBackdropClick({ ref: this.modal.id, close: () => this.modalService.close(this.modal.id), event });
    }
    this.modalService.close(this.modal.id);
  }

  private _insertComponent() {
    const givenCSSClasses = this.modal.options?.componentClass ? this.modal.options.componentClass.split(' ') : [];
    const componentClasses = ['the-default-display', ...givenCSSClasses];

    const ref = this.container.createComponent(this.modal.component);
    ref.location.nativeElement.classList.add(...componentClasses);
    if ('data' in ref.instance) {
      ref.setInput?.('data', this.modal.props);
    }
    if ('close' in ref.instance) {
      ref.setInput?.('close', () => this.modalService.close(this.modal.id));
    }
    if ('okClicked' in ref.instance) {
      ref.setInput?.('okClicked', (event: any) => this.okClick(event));
    }
    ref.changeDetectorRef.detectChanges();

    const focusTarget = this.modal.options?.focusOnOpen;
    if (focusTarget) {
      let element: HTMLElement | null = null;
      if (typeof focusTarget === 'string') {
        element = document.querySelector(focusTarget);
      } else if (focusTarget instanceof ElementRef) {
        element = focusTarget.nativeElement;
      }
      if (element) {
        requestAnimationFrame(() => {
          element.focus();
        });
      }
    }
  }

  private _setupEventListeners() {
    const overlay = this.modalOverlay.nativeElement;
    overlay.addEventListener('click', (event: MouseEvent) => this._onOverlayClick(event));
    overlay.addEventListener('keydown', (event: KeyboardEvent) => this._onOverlayKeydown(event), { passive: true });

    this._documentKeyDownListener = (event: KeyboardEvent) => {
      if (event.key === 'Escape') this._onEscape(event);
    };
    document.addEventListener('keydown', this._documentKeyDownListener, { passive: true });
  }
}
